import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { Firm } from '../../api/types';
import { safeGetItem, safeRemoveItem, safeSetItem } from '../../lib/safeStorage';
import { fetchFirme } from './api';

const STORAGE_KEY = 'firmaCurentaId';

export interface FirmaCurentaValue {
  /** Id of the firm every scoped screen operates on, or null while there is
   * genuinely no active firm to pick (onboarding). */
  firmaId: string | null;
  firma: Firm | null;
  /** Active firms only — the set the selector and the "pick a default" logic
   * both draw from. Inactive firms are never a valid "current firm". */
  firme: Firm[];
  isLoading: boolean;
  setFirmaId: (id: string) => void;
}

const FirmaCurentaContext = createContext<FirmaCurentaValue | null>(null);

/**
 * Owns "which firm is the user currently working in" for the whole app.
 * Mounted once, inside AuthGuard's authenticated branch (so it never runs
 * for an anonymous visitor), above AppLayout.
 *
 * Query-key convention this establishes for every firm-scoped list/detail
 * query from here on (Task 8b+): `['<resource>', firmaId, params]`, e.g.
 * `['clients', firmaId, { page, search }]` — firmaId is part of the key so
 * switching firms never serves another firm's cached page, and the request
 * itself sends `companyId: firmaId` as an explicit query param (the API has
 * no notion of "current firm" of its own).
 *
 * Persistence: `firmaCurentaId` in localStorage, via safeStorage.ts — every
 * access here is wrapped so a browser that blocks storage (locked-down
 * profile, full quota) degrades to "no persistence" instead of throwing out
 * of the `useState` initializer and crashing the whole authenticated tree.
 * On every load of the firm list, the stored id is checked against the
 * ACTIVE firms: valid → kept; stale/missing → falls back to the first
 * active firm; zero active firms → `firmaId` becomes null and the user is
 * bounced to the onboarding empty state on /firme (unless they're already
 * there, so that page can render without fighting this redirect).
 */
export function FirmaCurentaProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [firmaId, setFirmaIdState] = useState<string | null>(() => safeGetItem(STORAGE_KEY));

  const { data, isLoading } = useQuery({
    queryKey: ['firms'],
    queryFn: fetchFirme,
    staleTime: 60_000,
  });

  const firme = useMemo(() => (data?.items ?? []).filter((f) => f.isActive), [data]);

  useEffect(() => {
    if (isLoading) return;

    if (firme.length === 0) {
      if (firmaId !== null) {
        setFirmaIdState(null);
        safeRemoveItem(STORAGE_KEY);
      }
      if (location.pathname !== '/firme') {
        navigate('/firme?onboarding=1', { replace: true });
      }
      return;
    }

    const isStillValid = firmaId !== null && firme.some((f) => f.id === firmaId);
    if (!isStillValid) {
      const fallback = firme[0].id;
      setFirmaIdState(fallback);
      safeSetItem(STORAGE_KEY, fallback);
    }
  }, [isLoading, firme, firmaId, navigate, location.pathname]);

  const setFirmaId = useCallback((id: string) => {
    setFirmaIdState(id);
    safeSetItem(STORAGE_KEY, id);
  }, []);

  const firma = useMemo(() => firme.find((f) => f.id === firmaId) ?? null, [firme, firmaId]);

  const value = useMemo<FirmaCurentaValue>(
    () => ({ firmaId, firma, firme, isLoading, setFirmaId }),
    [firmaId, firma, firme, isLoading, setFirmaId],
  );

  return <FirmaCurentaContext.Provider value={value}>{children}</FirmaCurentaContext.Provider>;
}

export function useFirmaCurenta(): FirmaCurentaValue {
  const ctx = useContext(FirmaCurentaContext);
  if (!ctx) {
    throw new Error('useFirmaCurenta trebuie folosit în interiorul FirmaCurentaProvider.');
  }
  return ctx;
}
